/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/*
 * editor.component.ts
 * 5-2-2024 - Jelmer Jellema - Spin in het Web B.V.
 * Editorcomponent, wrapper om de quill editor library (via ngx-quill)
 *
 * GEBRUIK:
 *   <sihw-editor [(ngModel)]="inhoud"></sihw-editor>
 *
 * INPUTS:
 *   weergave: 'volledig' (standaard) | 'klein'
 *     - 'volledig': volledige toolbar met headers, kleuren, lijsten, afbeeldingen etc.
 *     - 'klein': minimale toolbar met alleen opmaak (bold, italic, underline, clean)
 *
 *   disabled: boolean (standaard false)
 *     - Zet de editor in readonly-modus
 *
 *   label-id: string (optioneel)
 *     - Koppelt een <label> aan de editor via aria-labelledby
 *
 *   useHtml: boolean (standaard false)
 *     - false (standaard): ngModel geeft een Quill Delta-object terug (aanbevolen)
 *       Delta is het native Quill-formaat, toekomstbestendig voor custom blots,
 *       veilig (geen HTML-sanitisatie nodig) en geschikt voor server-side verwerking.
 *     - true: ngModel geeft een HTML-string terug (legacy, voor achterwaartse compatibiliteit)
 *
 * AFHANKELIJKHEDEN:
 *   quill en ngx-quill moeten geïnstalleerd zijn in het host-project.
 *   De host-applicatie moet ook de Quill-stylesheet zelf laden. Voeg het volgende toe aan
 *   angular.json → projects → architect → build → options → styles:
 *     "node_modules/quill/dist/quill.snow.css"
 *   Of importeer het in styles.scss:
 *     @import 'quill/dist/quill.snow.css';
 *
 * COMBINEER MET:
 *   <sihw-edited> om de opgeslagen content weer te geven. Gebruik dezelfde waarde
 *   voor [useHtml] in zowel editor als edited.
 */

import {
  booleanAttribute,
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  Input,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {hash} from '../types';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import Quill from 'quill';
import {QuillEditorComponent, QuillModule} from 'ngx-quill';
import {BaseComponent} from '../base/basecomponent';
import ImageResize from './imageresize/ImageResize';
import {StyleableImageFormat} from './styleableimage';

@Component({
    standalone: true,
    selector: 'sihw-editor',
    templateUrl: './editor.component.html',
    styleUrls: ['./editor.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [QuillModule, FormsModule],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => EditorComponent), multi: true }
    ]
})
export class EditorComponent extends BaseComponent implements ControlValueAccessor {
  public model: any = null;
  public editorVisible: boolean = true;
  @ViewChild('Editor', {static: false}) editor!: QuillEditorComponent;
  @ViewChild('Container', {static: false}) container!: ElementRef;
  @Input('disabled') disabled: boolean = false;
  @Input('label-id') labelId: string | null = null;

  @Input('weergave') weergave: 'volledig' | 'klein' = 'volledig';
  @Input({transform: booleanAttribute}) useHtml: boolean = false;

  @HostBinding('attr.readonly') get hostReadonly() {
    return this.disabled ? true : null;
  }

  constructor() {
    super();
    EditorComponent.initQuill();
  }

  /** Het quill-formaat dat aan ngx-quill wordt doorgegeven: 'object' (Delta) of 'html' */
  get quillFormat(): 'object' | 'html' {
    return this.useHtml ? 'html' : 'object';
  }

  ///////////////////////////////////////// CONTROLVALUEACCESSOR //////////////////
  private meldGewijzigd: (_: any) => void = (_) => {
  };
  onTouched: () => void = () => {
  };

  writeValue(obj: any): void {
    this.model = obj;
  }

  registerOnChange(fn: any): void {
    this.meldGewijzigd = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  //////////////////////// Model /////////////////////////
  valueChanged() {
    this.log.debug(`Value nu`, this.model);
    this.meldGewijzigd(this.model);
  }

  /////////////////////////////////// EDITOR CONFIG ///////////////////////////////////
  public get quillmodules(): hash<any> {
    let toolbarbuttons: any[] = [];

    switch (this.weergave) {
      case "klein":
        toolbarbuttons = [
          [{size: []}],
          ['undo', 'redo'],
          ['bold', 'italic', 'underline', 'clean']
        ];
        break;
      case "volledig":
        toolbarbuttons = [
          [{header: [2, 3, false]}],
          [{size: []}],
          ['undo', 'redo'],
          ['bold', 'italic', 'underline', 'strike', {'script': 'sub'}, {'script': 'super'}, 'code', 'clean'],
          [{'color': []}, {'background': []}],
          [{'align': []}],
          [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
          ['link', 'image']
        ];
        break;
    }

    const handlers = {
      undo: function (this: any) {
        this.quill.history.undo();
      },
      redo: function (this: any) {
        this.quill.history.redo();
      },
    };

    return {
      toolbar: {
        container: toolbarbuttons,
        handlers: handlers
      },
      imageResize: {
        modules: ['Resize', 'DisplaySize', 'Toolbar']
      }
    };
  }

  initEditor() {
  }

  /**
   * Herinitialiseer de editor door hem even te verbergen en opnieuw te tonen.
   * Gebruik dit bij het wisselen van inputs die Quill niet automatisch oppikt
   * (zoals weergave of useHtml).
   */
  resetEditor() {
    this.editorVisible = false;
    setTimeout(() => {
      this.editorVisible = true;
    }, 0);
  }

  //////////////////////////////////// SETUP QUILL (static) //////////////////////////
  private static quillInitKlaar: boolean = false;

  private static initQuill() {
    if (this.quillInitKlaar) {
      return;
    }
    Quill.register("modules/imageResize", <any>ImageResize);
    Quill.register(StyleableImageFormat, true);
    this.quillInitKlaar = true;

    const icons = <any>Quill.import('ui/icons');
    icons.undo = `
        <svg viewbox="0 0 18 18">
            <polygon class="ql-fill ql-stroke" points="6 10 4 12 2 10 6 10"></polygon>
            <path class="ql-stroke" d="M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9"></path>
        </svg>`;
    icons.redo = `
        <svg viewbox="0 0 18 18">
            <polygon class="ql-fill ql-stroke" points="12 10 14 12 16 10 12 10"></polygon>
            <path class="ql-stroke" d="M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5"></path>
        </svg>
    `;
  }
}
